/**
 * Serviço de Artefatos do Processo — workflow_artifacts + workflow_artifact_versions
 *
 * Gerencia documentos versionados produzidos durante a execução de workflows.
 */
import { supabase } from '../../../lib/supabaseClient';

// ============================================================================
// TIPOS
// ============================================================================

export type ArtifactType = 'document' | 'form_output' | 'report' | 'attachment' | 'checklist_export';

export interface ProcessArtifact {
  id: string;
  instanceId: string;
  name: string;
  description: string | null;
  artifactType: ArtifactType;
  currentVersion: number;
  fileUrl: string;
  fileName: string;
  fileSizeBytes: number | null;
  fileMimeType: string | null;
  createdByName: string | null;
  createdAtStageName: string | null;
  lastEditedByName: string | null;
  lastEditedAtStageName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ArtifactVersion {
  id: string;
  artifactId: string;
  version: number;
  fileUrl: string;
  fileName: string;
  fileSizeBytes: number | null;
  editedByName: string | null;
  stageName: string | null;
  changeSummary: string | null;
  createdAt: string;
}

// ============================================================================
// MAPEAMENTO
// ============================================================================

function mapArtifact(row: any): ProcessArtifact {
  return {
    id: row.id,
    instanceId: row.instance_id,
    name: row.name,
    description: row.description,
    artifactType: row.artifact_type,
    currentVersion: row.current_version,
    fileUrl: row.file_url,
    fileName: row.file_name,
    fileSizeBytes: row.file_size_bytes,
    fileMimeType: row.file_mime_type,
    createdByName: row.created_by_name,
    createdAtStageName: row.created_at_stage_name,
    lastEditedByName: row.last_edited_by_name,
    lastEditedAtStageName: row.last_edited_at_stage_name,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapVersion(row: any): ArtifactVersion {
  return {
    id: row.id,
    artifactId: row.artifact_id,
    version: row.version,
    fileUrl: row.file_url,
    fileName: row.file_name,
    fileSizeBytes: row.file_size_bytes,
    editedByName: row.edited_by_name,
    stageName: row.stage_name,
    changeSummary: row.change_summary,
    createdAt: row.created_at,
  };
}

// ============================================================================
// QUERIES
// ============================================================================

export async function listInstanceArtifacts(instanceId: string): Promise<ProcessArtifact[]> {
  const { data, error } = await supabase
    .from('workflow_artifacts')
    .select('*')
    .eq('instance_id', instanceId)
    .order('updated_at', { ascending: false });

  if (error) {
    // Table may not exist yet — return empty gracefully
    if (error.code !== 'PGRST205') {
      console.warn('[artifactService] listInstanceArtifacts error:', error.code);
    }
    return [];
  }
  return (data ?? []).map(mapArtifact);
}

export async function getArtifactVersions(artifactId: string): Promise<ArtifactVersion[]> {
  const { data, error } = await supabase
    .from('workflow_artifact_versions')
    .select('*')
    .eq('artifact_id', artifactId)
    .order('version', { ascending: false });

  if (error) {
    console.warn('[artifactService] getArtifactVersions error:', error.code);
    return [];
  }
  return (data ?? []).map(mapVersion);
}

// ============================================================================
// MUTATIONS
// ============================================================================

interface CreateArtifactInput {
  organizationId: string;
  instanceId: string;
  name: string;
  description?: string;
  artifactType?: ArtifactType;
  fileUrl: string;
  fileName: string;
  fileSizeBytes?: number;
  fileMimeType?: string;
  createdBy: string;
  createdByName?: string;
  stageId?: string;
  stageName?: string;
}

export async function createArtifact(input: CreateArtifactInput): Promise<ProcessArtifact> {
  const { data, error } = await supabase
    .from('workflow_artifacts')
    .insert({
      organization_id: input.organizationId,
      instance_id: input.instanceId,
      name: input.name,
      description: input.description ?? null,
      artifact_type: input.artifactType ?? 'document',
      current_version: 1,
      file_url: input.fileUrl,
      file_name: input.fileName,
      file_size_bytes: input.fileSizeBytes ?? null,
      file_mime_type: input.fileMimeType ?? null,
      created_by: input.createdBy,
      created_by_name: input.createdByName ?? null,
      created_at_stage_id: input.stageId ?? null,
      created_at_stage_name: input.stageName ?? null,
      last_edited_by: input.createdBy,
      last_edited_by_name: input.createdByName ?? null,
      last_edited_at_stage_id: input.stageId ?? null,
      last_edited_at_stage_name: input.stageName ?? null,
    })
    .select()
    .single();

  if (error) throw error;

  // Create initial version record
  await supabase.from('workflow_artifact_versions').insert({
    artifact_id: data.id,
    organization_id: input.organizationId,
    version: 1,
    file_url: input.fileUrl,
    file_name: input.fileName,
    file_size_bytes: input.fileSizeBytes ?? null,
    file_mime_type: input.fileMimeType ?? null,
    edited_by: input.createdBy,
    edited_by_name: input.createdByName ?? null,
    stage_id: input.stageId ?? null,
    stage_name: input.stageName ?? null,
    change_summary: 'Versão inicial',
  });

  return mapArtifact(data);
}

interface UpdateArtifactInput {
  artifactId: string;
  organizationId: string;
  fileUrl: string;
  fileName: string;
  fileSizeBytes?: number;
  fileMimeType?: string;
  editedBy: string;
  editedByName?: string;
  stageId?: string;
  stageName?: string;
  changeSummary?: string;
}

export async function updateArtifactVersion(input: UpdateArtifactInput): Promise<ProcessArtifact> {
  // Get current version
  const { data: current, error: fetchErr } = await supabase
    .from('workflow_artifacts')
    .select('current_version')
    .eq('id', input.artifactId)
    .single();

  if (fetchErr) throw fetchErr;
  const newVersion = (current?.current_version ?? 0) + 1;

  // Update artifact
  const { data, error } = await supabase
    .from('workflow_artifacts')
    .update({
      current_version: newVersion,
      file_url: input.fileUrl,
      file_name: input.fileName,
      file_size_bytes: input.fileSizeBytes ?? null,
      file_mime_type: input.fileMimeType ?? null,
      last_edited_by: input.editedBy,
      last_edited_by_name: input.editedByName ?? null,
      last_edited_at_stage_id: input.stageId ?? null,
      last_edited_at_stage_name: input.stageName ?? null,
    })
    .eq('id', input.artifactId)
    .select()
    .single();

  if (error) throw error;

  // Create version record
  await supabase.from('workflow_artifact_versions').insert({
    artifact_id: input.artifactId,
    organization_id: input.organizationId,
    version: newVersion,
    file_url: input.fileUrl,
    file_name: input.fileName,
    file_size_bytes: input.fileSizeBytes ?? null,
    file_mime_type: input.fileMimeType ?? null,
    edited_by: input.editedBy,
    edited_by_name: input.editedByName ?? null,
    stage_id: input.stageId ?? null,
    stage_name: input.stageName ?? null,
    change_summary: input.changeSummary ?? null,
  });

  return mapArtifact(data);
}
